'use client'

import { NavigationQueryResult } from '@repo/sanity/types'
import { useMotionValueEvent, useScroll } from 'motion/react'
import { usePathname } from 'next/navigation'
import { createContext, ElementType, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface NavigationContextType {
  hidden: boolean
  logoElement: ElementType
  shouldUseInverse: (navigation: NavigationQueryResult) => boolean
  shouldHideOnPage: (navigation: NavigationQueryResult) => boolean
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined)

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const { scrollY } = useScroll()
  const pathname = usePathname()
  const [hidden, setHidden] = useState(false)
  const [prevScroll, setPrevScroll] = useState(0)
  const pathParts = pathname.split('/')
  const isHomepage = pathParts.length === 2
  const currentUrlSlug = isHomepage ? '/' : pathParts.filter(Boolean)?.pop()
  const logoElement: ElementType = isHomepage ? 'h1' : 'span'

  const updateNavigationVisibility = (latest: number, prev: number): void => {
    if (latest < prev) {
      setHidden(false)
    } else if (prev === 0) {
      setHidden(false)
    } else if (latest > 250 && latest > prev) {
      setHidden(true)
    }
  }

  useMotionValueEvent(scrollY, 'change', (latest: number) => {
    updateNavigationVisibility(latest, prevScroll)
    setPrevScroll(latest)
  })

  // Body CSS variables.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--spacing-miista-content-offset-dynamic-padded',
      hidden ? 'calc(var(--spacing-miista-navigation-margin-y) + var(--spacing-miista-page-padding))' : 'var(--spacing-miista-content-offset-static)',
    )

    document.documentElement.style.setProperty('--spacing-miista-content-offset-dynamic-tight', hidden ? '0' : 'var(--spacing-miista-content-offset-static)')
  }, [hidden])

  const shouldUseInverse = useCallback(
    (navigation: NavigationQueryResult) => {
      if (!currentUrlSlug) {
        return false
      }

      return navigation?.inverseOnPages?.filter(Boolean)?.includes(currentUrlSlug) || false
    },
    [currentUrlSlug],
  )

  const shouldHideOnPage = useCallback(
    (navigation: NavigationQueryResult) => {
      if (!currentUrlSlug) {
        return false
      }

      return navigation?.hideOnPages?.filter(Boolean)?.includes(currentUrlSlug) || false
    },
    [currentUrlSlug],
  )

  // Memoize the context value to ensure stable references
  const value = useMemo(
    () => ({
      hidden,
      logoElement,
      shouldUseInverse,
      shouldHideOnPage,
    }),
    [hidden, logoElement, shouldUseInverse, shouldHideOnPage],
  )

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
}

export const useNavigation = () => {
  const context = useContext(NavigationContext)

  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }

  return context
}
